import { ethers } from "ethers";

export const CARBON_V2_READ_MANIFEST = Object.freeze({
  contractsCommit: "bb78cbb3a9165771cfd21b93ae670442626d3a0e",
  releaseTag: "carbon-protocol-v2-2.0.0",
  abiChecksumFileSha256:
    "ee9a0dc0af66d8789736d6bd3ed1462e9528148195d5e9c67e5c1fcbfeb041ec",
  bytecodeChecksumFileSha256:
    "49051ba6c28048de46a5e7be4cdabb43b8a701f1c180a41ecb4e54b86016b199",
  supportedChainIds: [31337, 31338, 43113],
  investmentExecutionVariant: "batch",
  abiChecksums: Object.freeze({
    CarbonProjectFactoryV2:
      "ea07df8c77ee049e133d3f8d2a8d3231bdde35b57e72899b1f9c67fc80dc79a5",
    CarbonProjectEscrowV2:
      "0bab1b132f3e68700fd1fecd90fd5bda3312cb8ffb0bc4d66a005d1e6aa951df",
    CarbonInvestmentReceiptV2:
      "3589eb8f98c648a8e4371a27a8855b863dae8e48a6e07a552cab7007f97967e1",
    CarbonSettlementVaultV2:
      "37d28a4289e4474d12eb18990dd141aad5a4dd35637cbdbaafa76dc5f80ffc8f",
    VerifiedCarbonCredit1155V2:
      "ff33bcdbc9be793d99344d776c13774540e9acec9b363645db1174bdba68e5d9",
    ICarbonTransferPolicy:
      "a0f9bea1b5da9083d2085a15c10fbb0595825fe8b649a4b822f4aa6a3800f16e",
  }),
});

export const CARBON_V2_RELEASE_MANIFESTS = Object.freeze([
  CARBON_V2_READ_MANIFEST,
]);

export const resolveCarbonV2ReleaseManifest = ({
  releaseTag,
  contractsReleaseCommit,
} = {}) => {
  const manifest = CARBON_V2_RELEASE_MANIFESTS.find(
    (candidate) =>
      candidate.releaseTag === releaseTag &&
      candidate.contractsCommit === contractsReleaseCommit
  );
  if (!manifest) {
    throw new Error("current Carbon project uses an unsupported contracts release");
  }
  return manifest;
};

const ABI_FRAGMENTS = Object.freeze({
  CarbonProjectEscrowV2: Object.freeze([
    "function investBatch(bytes packedLegs)",
    "function maxProjectYear() view returns (uint8)",
    "function firstClaimTimestamp() view returns (uint64)",
    "function claimIntervalSeconds() view returns (uint32)",
    "function claimTimestampForYear(uint8 year) view returns (uint64)",
    "function predictedCreditsWadForYear(uint8 year) view returns (uint128)",
    "function stageCountForYear(uint8 year) view returns (uint8)",
    "function stageTermsFor(uint8 year,uint8 stageIndex) view returns (uint64 stageCost,uint64 stagePrice,uint128 raised,uint128 remainingCapacity,uint128 issuedUnits,uint8 settlementState)",
    "function stageFundingFor(uint8 year,uint8 stageIndex) view returns (tuple(uint128 raised,uint128 stablecoinFees,uint128 issuedUnits,uint128 receiptFeeUnits,uint128 refundRemaining,uint128 developerClaimable,uint128 developerPaid,uint128 refunded,uint128 compensationEarnedUnpaid,uint128 compensationPaid,bool refundFinalized,uint8 settlementState))",
    "function previewInvestment(uint8 year,uint8 stageIndex,uint128 grossUnits) view returns (uint256 stablePaid,uint128 stableFee,uint128 netPrincipal,uint128 receiptFee,uint128 investorUnits,uint256 tokenId)",
    "function certifier() view returns (address)",
    "function developer() view returns (address)",
    "function stablecoinTreasuryAddress() view returns (address)",
    "function receiptFeeRecipientAddress() view returns (address)",
    "function stablecoinFeeBps() view returns (uint16)",
    "function receiptTokenFeeBps() view returns (uint16)",
    "function transferPolicy() view returns (address)",
    "function stableCoin() view returns (address)",
    "function trustRegistry() view returns (address)",
    "function projectFailed() view returns (bool)",
  ]),
  CarbonInvestmentReceiptV2: Object.freeze([
    "function balanceOf(address account,uint256 id) view returns (uint256)",
    "function decimals() view returns (uint8)",
    "function decodeTokenId(uint256 tokenId) view returns (uint8 year,uint8 stage)",
  ]),
  CarbonSettlementVaultV2: Object.freeze([
    "function creditPool(uint8 year) view returns (uint256 creditTokenId,uint128 finalizedCredits,uint128 eligibleUnits,uint128 unitsBurned,uint128 creditsPaid,bytes32 bindingHash,uint8 state)",
    "function refundPool(uint256 receiptId) view returns (uint128 initialRefundPool,uint128 eligibleUnits,uint128 unitsBurned,uint128 stablePaid,uint8 state)",
    "function previewCreditClaim(uint256 receiptId,uint128 amount) view returns (uint128 creditsOut)",
    "function previewRefundClaim(uint256 receiptId,uint128 amount) view returns (uint128 stableOut)",
    "function verifiedCreditToken() view returns (address)",
  ]),
  VerifiedCarbonCredit1155V2: Object.freeze([
    "function settlementBinding(uint256 tokenId) view returns (tuple(address projectEscrow,bytes32 projectConfigHash,uint8 projectYear,uint16 registryId,uint16 vintageYear,bytes2 hostCountry,bytes32 registryReferenceHash,bytes32 routeHash,bytes32 evidenceHash,bool wholeTCO2e,address settlementVerifier,uint128 issuedWhole,uint128 retiredWhole,uint128 cancelledWhole,uint8 status))",
    "function settlementBindingHash(uint256 tokenId) view returns (bytes32)",
  ]),
  ICarbonTransferPolicy: Object.freeze([
    "function canExit(address holder,address recipient) view returns (bool)",
    "function canReceive(address recipient) view returns (bool)",
    "function canTransfer(address operator,address from,address to) view returns (bool)",
  ]),
});

const CARBON_V2_BATCH_INVESTMENT_INTERFACE = new ethers.utils.Interface([
  ABI_FRAGMENTS.CarbonProjectEscrowV2[0],
]);

export const encodeCarbonV2BatchInvestmentCalldata = (legs) => {
  const packedLegs = `0x${legs
    .map(({ receiptTokenId, units }) =>
      ((BigInt(receiptTokenId) << 128n) | BigInt(units))
        .toString(16)
        .padStart(64, "0")
    )
    .join("")}`;
  return CARBON_V2_BATCH_INVESTMENT_INTERFACE.encodeFunctionData(
    "investBatch",
    [packedLegs]
  ).toLowerCase();
};

export const getCarbonV2AbiFragment = (contractName) => {
  const fragment = ABI_FRAGMENTS[contractName];
  if (!fragment) {
    throw new Error(`unsupported Carbon V2 ABI fragment: ${contractName}`);
  }
  return fragment;
};

export const isSupportedCarbonV2Chain = (chainId) =>
  CARBON_V2_RELEASE_MANIFESTS.some((manifest) =>
    manifest.supportedChainIds.includes(Number(chainId))
  );

export const assertSupportedCarbonV2Chain = (chainId) => {
  if (!isSupportedCarbonV2Chain(chainId)) {
    throw new Error(`unsupported chain for Carbon V2: ${chainId}`);
  }
  return Number(chainId);
};
